import { connectRefinementList } from "instantsearch.js/es/connectors";
import { escapeHtml, runAfterFrame } from "./helpers";

type SortByDirection =
    | "count:asc"
    | "count:desc"
    | "name:asc"
    | "name:desc"
    | "isRefined";

export interface MenuStyleRefinementOptions {
    placeholder?: string;
    searchable?: boolean;
    showMore?: boolean;
    showMoreLimit?: number;
    limit?: number;
    sortBy?: SortByDirection[];
    /** true → square checkbox (AND operator), false → rounded radio (OR) */
    multiple?: boolean;
    operator?: "and" | "or";
    transformItems?: (items: any[]) => any[];
}

function resolveFacetContainers(selector: string | string[]): HTMLElement[] {
    const selectors = Array.isArray(selector) ? selector : [selector];
    return selectors.flatMap((sel) => {
        const el = document.querySelector<HTMLElement>(sel);
        return el ? [el] : [];
    });
}

/**
 * Custom refinement list that outputs the same HTML as Vue's Menu.vue
 * (RefinementList__* classes, rounded or square checkboxes, has-selected dimming).
 * Used for model and options so Blade and Vue share the same DOM structure.
 */
export function createMenuStyleRefinementWidget(
    containerSelector: string | string[],
    attribute: string,
    opts: MenuStyleRefinementOptions = {},
) {
    const containers = resolveFacetContainers(containerSelector);
    if (!containers.length) return null;

    const {
        placeholder = "Rechercher…",
        searchable = false,
        showMore = false,
        showMoreLimit = 20,
        limit = 10,
        sortBy,
        multiple = false,
        operator = "or",
        transformItems,
    } = opts;

    let latestRefine: ((value: string) => void) | null = null;
    let listenersBound = false;
    let localSearchQuery = "";

    const scheduleFacetRender = (
        renderOptions: Parameters<
            Parameters<typeof connectRefinementList>[0]
        >[0],
    ) => {
        (
            renderOptions as typeof renderOptions & {
                instantSearchInstance: { scheduleRender: () => void };
            }
        ).instantSearchInstance.scheduleRender();
    };

    const customRefinement = connectRefinementList<{ container: string }>(
        (renderOptions, isFirstRender) => {
            const {
                items: rawItems,
                refine,
                searchForItems,
                isShowingMore,
                canToggleShowMore,
                toggleShowMore,
            } = renderOptions;
            latestRefine = refine;

            const items = transformItems ? transformItems(rawItems) : rawItems;
            const hasSelected = items.some((i) => i.isRefined);

            const q = localSearchQuery.trim().toLowerCase();
            const displayItems = q
                ? items.filter((item) =>
                      item.label.toLowerCase().includes(q),
                  )
                : items;

            const listHtml = displayItems.length
                ? displayItems
                      .map(
                          (item) =>
                              `<label class="RefinementList${
                                  item.isRefined
                                      ? " RefinementList--selected"
                                      : ""
                              }">` +
                              `<span><span class="RefinementList__labelText">${escapeHtml(
                                  item.label,
                              )}</span>` +
                              `<span class="RefinementList__count">(${item.count.toLocaleString(
                                  "fr-FR",
                              )})</span></span>` +
                              `<input type="checkbox" class="RefinementList__checkbox${
                                  !multiple
                                      ? " RefinementList__checkbox--rounded"
                                      : ""
                              }"` +
                              `${
                                  item.isRefined ? " checked" : ""
                              } data-value="${escapeHtml(
                                  item.value,
                              )}" /></label>`,
                      )
                      .join("")
                : `<span class="no-results-label">Aucun résultat</span>`;

            const showMoreHtml = canToggleShowMore
                ? `<button type="button" class="RefinementList__showMore">${
                      isShowingMore ? "Voir moins" : "Voir plus"
                  }</button>`
                : "";

            const markup =
                (searchable
                    ? `<div class="RefinementList__search input__field input__field--facette"><input type="search" class="input input--search" placeholder="${escapeHtml(
                          placeholder,
                      )}" autocomplete="off" data-facet-search /></div>`
                    : "") +
                `<div class="RefinementList__wrapper${
                    hasSelected && !multiple
                        ? " RefinementList__wrapper--has-selected"
                        : ""
                }">${listHtml}${showMoreHtml}</div>`;

            for (const container of containers) {
                if (isFirstRender) {
                    container.innerHTML = markup;
                } else {
                    const wrap = container.querySelector<HTMLElement>(
                        ".RefinementList__wrapper",
                    );
                    if (wrap) {
                        wrap.classList.toggle(
                            "RefinementList__wrapper--has-selected",
                            hasSelected && !multiple,
                        );
                        wrap.innerHTML = listHtml + showMoreHtml;
                    }
                }
            }

            if (!listenersBound && isFirstRender) {
                listenersBound = true;

                // Bind search on every mount (sidebar + action-bar). Listening
                // only on containers[0] left the action-bar input inert when it
                // was the secondary selector — which is the one users type in.
                if (searchable) {
                    const onFacetSearchInput = (event: Event) => {
                        const input = event.target as HTMLInputElement;
                        if (
                            !(input instanceof HTMLInputElement) ||
                            !input.matches("[data-facet-search]")
                        ) {
                            return;
                        }
                        localSearchQuery = input.value;
                        containers.forEach((container) => {
                            const mirror =
                                container.querySelector<HTMLInputElement>(
                                    "[data-facet-search]",
                                );
                            if (mirror && mirror !== input) {
                                mirror.value = input.value;
                            }
                        });
                        searchForItems(input.value);
                        scheduleFacetRender(renderOptions);
                    };

                    containers.forEach((container) => {
                        container.addEventListener("input", onFacetSearchInput);
                    });
                }

                // Checkboxes refine on `change` only (a label/checkbox tap
                // always fires it) and defer the InstantSearch work past the
                // next paint so the tap's INP window stays short. `click`
                // must not also refine: with the deferred refine the input
                // stays in the DOM, so both events would fire and the two
                // toggles would cancel each other.
                const onShowMoreClick = (event: Event) => {
                    const target = event.target as HTMLElement;
                    if (target.classList.contains("RefinementList__showMore")) {
                        runAfterFrame(() => toggleShowMore());
                    }
                };
                const onCheckboxChange = (event: Event) => {
                    const input = event.target as HTMLInputElement;
                    if (!input.classList.contains("RefinementList__checkbox")) {
                        return;
                    }
                    const val = input.dataset.value;
                    if (val) runAfterFrame(() => latestRefine?.(val));
                };

                containers.forEach((container) => {
                    container.addEventListener("change", onCheckboxChange);
                    container.addEventListener("click", onShowMoreClick);
                });
            }
        },
    );

    return customRefinement({
        container: containers[0].id
            ? `#${containers[0].id}`
            : (Array.isArray(containerSelector)
                  ? containerSelector[0]
                  : containerSelector),
        attribute,
        operator,
        limit,
        showMore,
        showMoreLimit,
        ...(sortBy ? { sortBy } : {}),
    });
}
